import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Search,
  Users,
  GraduationCap,
  UserCheck,
  Users2,
  Loader2,
  ChevronRight,
  MapPin,
} from 'lucide-react';
import {
  getInstitutionsWithUserCounts,
  type InstitutionWithCounts,
} from '@/services/superAdminUserService';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';

export const SuperAdminUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, error: showError } = useToast();
  const [institutions, setInstitutions] = useState<InstitutionWithCounts[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchInstitutions();
  }, []);

  const fetchInstitutions = async () => {
    setIsLoading(true);
    try {
      const data = await getInstitutionsWithUserCounts();
      setInstitutions(data);
    } catch (error) {
      console.error('Error fetching institutions:', error);
      showError('Erreur', 'Impossible de charger les etablissements');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredInstitutions = institutions.filter((institution) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      institution.name.toLowerCase().includes(searchLower) ||
      institution.city?.toLowerCase().includes(searchLower) ||
      institution.slug.toLowerCase().includes(searchLower)
    );
  });

  const handleInstitutionClick = (institutionId: string) => {
    navigate(`/admin/etablissements/utilisateurs/${institutionId}`);
  };

  // Calculate totals
  const totals = institutions.reduce(
    (acc, inst) => ({
      staff: acc.staff + inst.staffCount,
      professors: acc.professors + inst.professorCount,
      students: acc.students + inst.studentCount,
      parents: acc.parents + inst.parentCount,
    }),
    { staff: 0, professors: 0, students: 0, parents: 0 }
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Users className="h-6 w-6 text-indigo-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
          </div>
          <p className="text-gray-500 ml-12">
            Gerez les utilisateurs de tous les etablissements
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <UserCheck className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Personnel</p>
              <p className="text-2xl font-bold text-gray-900">{totals.staff.toLocaleString()}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <GraduationCap className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Professeurs</p>
              <p className="text-2xl font-bold text-gray-900">{totals.professors.toLocaleString()}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Etudiants</p>
              <p className="text-2xl font-bold text-gray-900">{totals.students.toLocaleString()}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-lg">
              <Users2 className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Parents</p>
              <p className="text-2xl font-bold text-gray-900">{totals.parents.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un etablissement..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Institutions Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
          </div>
        ) : filteredInstitutions.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              Aucun etablissement trouve
            </h3>
            <p className="text-gray-500">
              {searchTerm
                ? 'Aucun etablissement ne correspond a votre recherche'
                : 'Aucun etablissement actif pour le moment'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredInstitutions.map((institution) => (
              <div
                key={institution.id}
                onClick={() => handleInstitutionClick(institution.id)}
                className="bg-white rounded-xl border border-gray-200 p-5 cursor-pointer hover:border-indigo-300 hover:shadow-md transition-all group"
              >
                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-white">
                      {institution.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                      {institution.name}
                    </h3>
                    {institution.city && (
                      <div className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>{institution.city}</span>
                      </div>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
                </div>

                {/* User Counts */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-blue-600" />
                      <span className="text-xs text-gray-500">Personnel</span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 mt-1">
                      {institution.staffCount}
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="h-4 w-4 text-purple-600" />
                      <span className="text-xs text-gray-500">Professeurs</span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 mt-1">
                      {institution.professorCount}
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-green-600" />
                      <span className="text-xs text-gray-500">Etudiants</span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 mt-1">
                      {institution.studentCount}
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Users2 className="h-4 w-4 text-amber-600" />
                      <span className="text-xs text-gray-500">Parents</span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 mt-1">
                      {institution.parentCount}
                    </p>
                  </div>
                </div>

                {/* Total */}
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-sm text-gray-500">Total utilisateurs</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {institution.totalUsers.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Results count */}
        {!isLoading && filteredInstitutions.length > 0 && (
          <p className="text-sm text-gray-500 text-center mt-6">
            {filteredInstitutions.length} etablissement{filteredInstitutions.length > 1 ? 's' : ''} affiche{filteredInstitutions.length > 1 ? 's' : ''}
          </p>
        )}
      </div>
    </div>
  );
};

export default SuperAdminUsersPage;
